import { type LineItem, loadStripe, type ShippingRate, type Stripe } from '@stripe/stripe-js';

import type { ProductList, PurchaseState } from 'types/checkout';
import { DIGITAL_GC_ONLY_CART } from 'constants/cartTypes';
import { toStripePrice } from 'helpers/currency';
import { formatShippingOptionDescription } from 'helpers/shipping';

interface SelectedShippingRate extends ShippingRate {
  isSelected: boolean;
}

let stripePromise: Promise<Stripe | null> | null = null;

export const CALCULATING_SHIPPING_RATE: ShippingRate = {
  amount: 0,
  displayName: 'Calculating Shipping Rates',
  id: '0'
};

/**
 * Get Stripe instance
 */
export const getStripe = (apiKey: string): Promise<Stripe | null> => {
  if (!stripePromise) {
    stripePromise = loadStripe(apiKey);
  }
  return stripePromise;
};

/**
 * Map shipping rates to Stripe format
 */
export const mapStripeShippingRates = (purchaseState: PurchaseState): ShippingRate[] => {
  const { cartType, groupDeliveryOptions = [], shipmentSpeed, shippingBenefitReason = 0 } = purchaseState || {};

  // Check if this is a digital gift card only cart
  const isEGCOnly = cartType === DIGITAL_GC_ONLY_CART;

  if (isEGCOnly) {
    return [];
  }

  if (!groupDeliveryOptions.length) {
    return [CALCULATING_SHIPPING_RATE];
  }

  const deliveryOptions: SelectedShippingRate[] = groupDeliveryOptions.flatMap(
    option =>
      option.deliveryOptions?.map(({ id, name, price, displayString }) => ({
        amount: toStripePrice(price),
        deliveryEstimate: displayString,
        displayName: formatShippingOptionDescription(shippingBenefitReason, name, price),
        id,
        isSelected: name === shipmentSpeed
      })) || []
  );

  // Sort by selected option first
  deliveryOptions.sort((a, b) => Number(b.isSelected) - Number(a.isSelected));

  return deliveryOptions;
};

/**
 * Map products to Stripe line items format
 */
export const mapProductsToLineItems = (purchaseStatus: PurchaseState): LineItem[] => {
  const {
    productList,
    chargeSummary: { afterTaxDiscounts, coupons, estimatedTax, shippingCharge }
  } = purchaseStatus;

  if (!productList) {
    return [];
  }

  const lineItems = productList.map(product => ({
    amount: toStripePrice(product.price * product.quantity),
    name: product.quantity > 1 ? `${product.productName} x ${product.quantity}` : product.productName
  }));

  if (estimatedTax > 0) {
    lineItems.push({ amount: toStripePrice(estimatedTax), name: 'Tax' });
  }

  if (shippingCharge > 0) {
    lineItems.push({ amount: toStripePrice(shippingCharge), name: 'Shipping' });
  }

  if (afterTaxDiscounts?.length) {
    afterTaxDiscounts.forEach(discount => {
      if (discount.amountUsed) {
        lineItems.push({ amount: toStripePrice(-1 * discount.amountUsed), name: 'Discount' });
      }
    });
  }

  if (coupons?.length) {
    coupons.forEach(coupon => {
      if (coupon.amountUsed) {
        lineItems.push({ amount: toStripePrice(-1 * coupon.amountUsed), name: 'Coupon' });
      }
    });
  }

  return lineItems;
};

export const mapProductsToLineItemIds = (productList?: ProductList[]): string[] => {
  if (!productList) {
    return [];
  }

  return productList.map(product => product.lineItemId);
};
